<template>
  <div class="WalkPuzzle">
    <div
      class="puzzle-board"
      :class="{ 'no-timer': isIdle && !isSettingsMode && !isWalkingReds, 'settings-mode': isSettingsMode }"
      :style="{
        '--timer-fraction': isWalkingReds ? 1 : Math.min(timerFraction, 1),
        '--timer-overflow': isWalkingReds ? 0 : Math.max(0, Math.min(timerFraction - 1, 1)),
        '--timer-color': timerColor,
        '--tip-color': isWalkingReds ? timerColor : timerFraction >= 1 ? timerColor : 'oklch(0.82 0.17 80)',
        '--glow-color':
          isIdle || isSettingsMode
            ? 'transparent'
            : isBreak
              ? 'oklch(0.78 0.24 var(--hue-2) / 0.08)'
              : 'rgba(255, 255, 255, 0.06)',
      }"
    >
      <div v-if="timerFraction > 1 && !isIdle && !isSettingsMode && !isWalkingReds" class="timer-overflow-ring" />
      <div class="board-inner">
        <!-- Settings mode: Walkfield chessboard grid -->
        <div
          v-if="isSettingsMode"
          class="walkfield-grid"
          :class="{ loading: isLoading, zoomed: zoomLevel < 8, inspect: squaresInspect }"
          :style="{ '--zoom-n': zoomLevel }"
        >
          <template v-if="zoomLevel === 8">
            <template v-for="row in 8" :key="row">
              <div
                v-for="col in 8"
                :key="`${row}-${col}`"
                class="walkfield-cell"
                :class="{
                  selected: selectedField?.speed === 9 - row && selectedField?.band === BAND_LETTERS[col - 1],
                }"
                :style="getFieldCellStyle(9 - row, BAND_LETTERS[col - 1])"
                @click="handleFieldClick(9 - row, BAND_LETTERS[col - 1])"
              >
                <!-- prettier-ignore -->
                <span
                  v-if="selectedField?.speed === 9 - row && selectedField?.band === BAND_LETTERS[col - 1]"
                  class="walkfield-selected-label"
                >{{ BAND_LETTERS[col - 1].toUpperCase() }}{{ 9 - row }}</span>
                <template v-else>
                  <span v-if="col === 1" class="walkfield-row-label">{{ 9 - row }}</span>
                  <span v-if="row === 8" class="walkfield-col-label">{{ BAND_LETTERS[col - 1] }}</span>
                </template>
              </div>
            </template>
          </template>
          <template v-else>
            <div
              v-for="field in visibleZoomFields"
              :key="`${field.speed}${field.band}`"
              class="walkfield-cell"
              :class="{
                selected: selectedField?.speed === field.speed && selectedField?.band === field.band,
              }"
              :style="getFieldCellStyle(field.speed, field.band)"
              @click="handleFieldClick(field.speed, field.band)"
            >
              <span
                class="walkfield-zoom-label"
                :class="{
                  selected: selectedField?.speed === field.speed && selectedField?.band === field.band,
                  dark: field.score / 100 < 0.4,
                }"
              >
                {{ field.band.toUpperCase() }}{{ field.speed }}
              </span>
            </div>
          </template>
          <div v-if="isLoading" class="settings-spinner-overlay">
            <div class="cover-spinner" />
          </div>
        </div>

        <!-- Active mode: Chess puzzle board -->
        <GamChessboard
          v-if="!isSettingsMode && puzzle.isReady.value"
          :key="boardKey"
          :fen="puzzle.boardFen.value"
          :mode="GamChessboardMode.PLAY"
          :orientation="puzzle.orientation.value"
          :is-interactive="canInteract"
          :last-move-from="puzzle.lastMoveFrom.value"
          :last-move-to="puzzle.lastMoveTo.value"
          is-animated
          skip-promotion
          @move="handleMove"
        />

        <Transition name="fade">
          <div v-if="showOverlay" class="board-overlay" />
        </Transition>

        <template v-if="!isSettingsMode">
          <Transition name="fade">
            <div v-if="needsLocationUpdate" class="location-overlay" @click="emit('retry-location')">
              <span class="location-overlay-text">Tap to update location</span>
            </div>
          </Transition>

          <Transition name="fade">
            <div v-if="submitRetrying" class="location-overlay submit-retrying">
              <span class="submit-retry-spinner" />
            </div>
          </Transition>

          <Transition name="fade">
            <div v-if="submitFailed" class="location-overlay" @click="emit('retry-submit')">
              <span class="location-overlay-text">Reload</span>
            </div>
          </Transition>

          <Transition name="fade">
            <div v-if="revealFailed" class="location-overlay" @click="emit('reveal-dismiss')">
              <span class="location-overlay-text">Reveal failed. Tap to continue</span>
            </div>
          </Transition>
        </template>

        <div v-if="endState === 'timed_out' && !isSettingsMode" class="end-overlay end-overlay-timeout">
          <span class="end-overlay-text">0.0</span>
        </div>

        <div v-else-if="endState === 'completed' && !isSettingsMode" class="end-overlay end-overlay-completed">
          <img :src="puzzleIcon" class="end-overlay-icon" alt="" />
          <span class="end-overlay-text end-overlay-text-dark">50</span>
        </div>

        <div v-else-if="isWarmup && !isSettingsMode" class="board-cover warmup-cover" @click="handleWarmupTap">
          <div class="cover-checkerboard" />
          <Transition name="warmup-fade">
            <div v-if="!warmupReady" class="warmup-overlay-layer">
              <div class="warmup-dim" />
              <div class="warmup-content">
                <span class="warmup-speed" :class="speedAtTarget ? 'speed-green' : 'speed-red'">
                  {{ targetSpeedDisplay }}
                </span>
                <div class="warmup-dots">
                  <span class="warmup-dot filled" />
                  <span v-for="i in 5" :key="i" class="warmup-dot" :class="{ filled: warmupDotsFilled >= i }" />
                </div>
              </div>
            </div>
          </Transition>
          <span v-if="warmupReady && !warmupLoading" class="warmup-tap-hint">{{
            localize('walk.warmup.tap_board')
          }}</span>
          <div v-if="warmupLoading" class="cover-spinner" />
        </div>

        <div
          v-else-if="isBreak && !isSettingsMode"
          class="board-cover"
          :class="{ loading: breakLoading }"
          @click="handleBreakTap"
        >
          <div class="cover-checkerboard" />
          <Transition name="warmup-fade">
            <div v-if="breakNeedsWarmup && !breakWarmupReady" class="warmup-overlay-layer">
              <div class="warmup-dim" />
              <div class="warmup-content">
                <span class="warmup-speed" :class="speedAtTarget ? 'speed-green' : 'speed-red'">
                  {{ targetSpeedDisplay }}
                </span>
                <div class="warmup-dots">
                  <span class="warmup-dot filled" />
                  <span v-for="i in 5" :key="i" class="warmup-dot" :class="{ filled: breakWarmupDotsFilled >= i }" />
                </div>
              </div>
            </div>
          </Transition>
          <span v-if="breakNeedsWarmup && breakWarmupReady && !breakLoading" class="warmup-tap-hint">{{
            localize('walk.warmup.tap_board')
          }}</span>
          <div v-if="!breakNeedsWarmup && progressDots.length" class="progress-dots">
            <span v-for="(dot, i) in progressDots" :key="i" class="progress-dot" :class="dot" />
          </div>
          <div v-if="breakLoading" class="cover-spinner" />
        </div>

        <div
          v-else-if="isIdle && !isSettingsMode"
          class="board-cover"
          :class="{ loading: idleLoading }"
          @click="handleIdleTap"
        >
          <div class="cover-checkerboard" />
          <div v-if="idleLoading" class="cover-spinner" />
        </div>

        <div v-if="showRedsOverlay" class="board-cover reds-cover" @click="emit('walk-reds')">
          <div class="cover-checkerboard" />
          <span class="reds-tap-hint">Reds</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import { useWalkStore } from '@/stores/walk.store';
import GamChessboard from '@/views/components/gam-chessboard/GamChessboard.vue';
import { GamChessboardMode } from '@/views/composables/constants/components/gamChessboard.constants';
import { usePuzzleEngine, type PuzzleData } from '@/views/composables/hooks/usePuzzleEngine';
import { computed, ref, watch, type Ref } from 'vue';

interface RoundLogEntry {
  roundNumber: number;
  isCorrect: boolean | null;
}

export type WalkZoomLevel = 8 | 4 | 2 | 1;

interface RankedField {
  speed: number;
  band: string;
  score: number;
}

const puzzleIcon = new URL('../../../../ui/icons/puzzle-grid.svg', import.meta.url).href;
const walkStore = useWalkStore();

const props = defineProps<{
  puzzleData: PuzzleData | null;
  isSubmitting: boolean;
  isBreak: boolean;
  isIdle: boolean;
  isWarmup?: boolean;
  warmupProgress?: number;
  warmupReady?: boolean;
  breakNeedsWarmup?: boolean;
  breakWarmupReady?: boolean;
  currentSpeedKmh?: number;
  targetSpeedKmh?: number;
  isSettingsMode?: boolean;
  isLoading?: boolean;
  selectedField?: { speed: number; band: string };
  roundLog?: RoundLogEntry[];
  showProgress?: boolean;
  needsLocationUpdate: boolean;
  submitFailed: boolean;
  submitRetrying: boolean;
  revealFailed: boolean;
  endState: 'timed_out' | 'completed' | 'red_walk_complete' | null;
  timerFraction: number;
  showRedsOverlay?: boolean;
  isWalkingReds?: boolean;
  zoomLevel?: WalkZoomLevel;
  squaresInspect?: boolean;
}>();

const BAND_LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const BRILLIANT_COMPLETE = 100;

function getFieldCellStyle(speed: number, band: string): Record<string, string> {
  if (props.selectedField?.speed === speed && props.selectedField?.band === band) return {};
  const key = `${speed}${band}`;
  const score = walkStore.fieldBrilliantMap.get(key) ?? 0;
  if (score === 0) return {};

  if (props.squaresInspect) {
    const ch = Math.round((score / 100) * 255);
    return { background: `rgb(${ch}, ${ch}, ${ch})` };
  }

  // Walked fields fade from black to the canopy: 100% shows the full photo.
  return { background: `rgba(0, 0, 0, ${Math.max(0, 1 - score / 100)})` };
}

const emit = defineEmits<{
  move: [moveUci: string];
  'break-tap': [];
  'warmup-tap': [];
  'walk-reds': [];
  start: [];
  'retry-location': [];
  'retry-submit': [];
  'reveal-dismiss': [];
  'field-select': [field: { speed: number; band: string }];
  'zoom-select': [field: { speed: number; band: string }];
}>();

const zoomLevel = computed<WalkZoomLevel>(() => props.zoomLevel ?? 8);
const rankedFields: Ref<RankedField[] | null> = ref(null);

function shuffleInPlace<T>(items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}

function shuffleWithinBuckets(fields: RankedField[], keyOf: (f: RankedField) => number): RankedField[] {
  const result: RankedField[] = [];
  let i = 0;
  while (i < fields.length) {
    const key = keyOf(fields[i]);
    let j = i + 1;
    while (j < fields.length && keyOf(fields[j]) === key) j++;
    result.push(...shuffleInPlace(fields.slice(i, j)));
    i = j;
  }
  return result;
}

// Chebyshev distance treats all 8 neighbouring fields as equally close, which
// fits the board grid better than Manhattan distance.
function fieldDistance(a: RankedField, b: RankedField): number {
  return Math.max(Math.abs(a.speed - b.speed), Math.abs(BAND_LETTERS.indexOf(a.band) - BAND_LETTERS.indexOf(b.band)));
}

function buildRankedFields(): RankedField[] {
  const walked: RankedField[] = [];
  const untouched: RankedField[] = [];
  const finished: RankedField[] = [];

  for (let speed = 1; speed <= 8; speed++) {
    for (const band of BAND_LETTERS) {
      const key = `${speed}${band}`;
      const hasData = walkStore.fieldBrilliantMap.has(key);
      const score = walkStore.fieldBrilliantMap.get(key) ?? 0;
      const field = { speed, band, score };
      if (score >= BRILLIANT_COMPLETE) finished.push(field);
      else if (hasData) walked.push(field);
      else untouched.push(field);
    }
  }

  // Walked-but-unfinished: closest to completion first, ties shuffled.
  walked.sort((a, b) => b.score - a.score);
  const rankedWalked = shuffleWithinBuckets(walked, (f) => f.score);

  // Untouched: stay near the user's demonstrated capability. Rank by proximity
  // to the nearest field with any history, ties shuffled. With no history at
  // all this collapses to a single fully-shuffled bucket.
  const walkedForDistance = [...walked, ...finished];
  const proximity = new Map<RankedField, number>();
  for (const field of untouched) {
    let nearest = Infinity;
    for (const ref of walkedForDistance) {
      nearest = Math.min(nearest, fieldDistance(field, ref));
    }
    proximity.set(field, nearest);
  }
  untouched.sort((a, b) => (proximity.get(a) ?? 0) - (proximity.get(b) ?? 0));
  const rankedUntouched = shuffleWithinBuckets(untouched, (f) => proximity.get(f) ?? 0);

  return [...rankedWalked, ...rankedUntouched, ...shuffleInPlace(finished)];
}

function pickZoomSelection(fields: RankedField[]): RankedField | null {
  if (!fields.length) return null;
  const unfinished = fields.filter((f) => f.score < BRILLIANT_COMPLETE);
  if (unfinished.length) return unfinished[0];
  return fields[Math.floor(Math.random() * fields.length)];
}

const visibleZoomFields = computed(() => {
  if (zoomLevel.value === 8 || !rankedFields.value) return [];
  const n = zoomLevel.value;
  return rankedFields.value.slice(0, n * n);
});

watch(
  zoomLevel,
  (level) => {
    if (!props.isSettingsMode || level === 8) return;
    if (!rankedFields.value) {
      rankedFields.value = buildRankedFields();
    }
    const visible = rankedFields.value.slice(0, level * level);
    const pick = pickZoomSelection(visible);
    if (pick) emit('zoom-select', { speed: pick.speed, band: pick.band });
  },
  { flush: 'sync' },
);
watch(
  () => props.isSettingsMode,
  (settingsMode) => {
    if (!settingsMode) rankedFields.value = null;
  },
);

const boardKey = ref(0);
const breakLoading = ref(false);
const idleLoading = ref(false);
const warmupLoading = ref(false);

const speedAtTarget = computed(() => (props.currentSpeedKmh ?? 0) >= (props.targetSpeedKmh ?? Infinity));
const targetSpeedDisplay = computed(() => `${Math.round(props.targetSpeedKmh ?? 0)}km/h`);
const warmupDotsFilled = computed(() => Math.floor((props.warmupProgress ?? 0) * 5));
const breakWarmupDotsFilled = computed(() => Math.floor((props.warmupProgress ?? 0) * 5));

type ProgressDot = 'correct' | 'wrong' | 'timeout';

const progressDots = computed<ProgressDot[]>(() => {
  if (!props.showProgress || !props.roundLog?.length) return [];
  let dots: ProgressDot[] = [];
  let streakIsCorrect: boolean | null = null;
  for (const entry of props.roundLog) {
    const isCorrect = entry.isCorrect === true;
    if (isCorrect !== streakIsCorrect) {
      dots = [];
      streakIsCorrect = isCorrect;
    }
    dots.push(isCorrect ? 'correct' : entry.isCorrect === false ? 'wrong' : 'timeout');
  }
  return dots;
});

function handleFieldClick(speed: number, band: string) {
  emit('field-select', { speed, band });
}

const puzzle = usePuzzleEngine(() => props.puzzleData);

const canInteract = computed(
  () =>
    puzzle.canInteract.value &&
    !props.isSubmitting &&
    !props.submitFailed &&
    !props.submitRetrying &&
    !props.isBreak &&
    !props.isIdle,
);
const showOverlay = computed(() => props.isSubmitting);

const timerColor = computed(() => {
  if (props.isSettingsMode) return 'transparent';
  if (props.isIdle) return 'transparent';
  if (props.endState) return 'transparent';
  if (props.isWarmup && props.timerFraction <= 0) return 'transparent';
  if (props.isWarmup) return speedAtTarget.value ? 'oklch(0.72 0.17 145)' : 'oklch(0.65 0.2 25)';
  if (props.isBreak) return 'var(--color-pink)';
  return puzzle.playerColor.value === 'w' ? '#ffffff' : '#000000';
});

watch(
  () => props.puzzleData?.id,
  (newId, oldId) => {
    if (newId != null && newId !== oldId) {
      boardKey.value++;
    }
  },
);

watch(
  () => props.isWarmup,
  (val) => {
    if (!val) warmupLoading.value = false;
  },
);

watch(
  () => props.isBreak,
  (val) => {
    if (!val) breakLoading.value = false;
  },
);

watch(
  () => props.isIdle,
  (val) => {
    if (!val) idleLoading.value = false;
  },
);

function handleWarmupTap() {
  if (!props.warmupReady || warmupLoading.value) return;
  warmupLoading.value = true;
  emit('warmup-tap');
}

function handleBreakTap() {
  if (breakLoading.value) return;
  if (props.breakNeedsWarmup && !props.breakWarmupReady) return;
  breakLoading.value = true;
  emit('break-tap');
}

function handleIdleTap() {
  if (idleLoading.value) return;
  idleLoading.value = true;
  emit('start');
}

function handleMove(move: { from: string; to: string; promotion?: string }) {
  puzzle.checkMove(move.from, move.to, move.promotion);
  const uci = move.from + move.to + (move.promotion || '');
  emit('move', uci);
}

defineExpose({
  resetLoading: () => {
    idleLoading.value = false;
    breakLoading.value = false;
    warmupLoading.value = false;
  },
  setIdleLoading: (val: boolean) => {
    idleLoading.value = val;
  },
});
</script>

<style>
@property --timer-fraction {
  syntax: '<number>';
  inherits: true;
  initial-value: 1;
}

@property --timer-overflow {
  syntax: '<number>';
  inherits: true;
  initial-value: 0;
}
</style>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.WalkPuzzle {
  position: relative;
  width: 100%;
  height: 100%;
}

.puzzle-board {
  --timer-fraction: 1;
  --timer-overflow: 0;
  --timer-color: #ffffff;
  --tip-color: oklch(0.82 0.17 80);
  --overflow-tip: oklch(0.82 0.17 80);

  transition: box-shadow 0.5s ease;

  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: calc(var(--radius-x-small) + 5px);
  box-shadow:
    0 0 0 0.5px rgba(255, 255, 255, 0.12),
    0 0 40px 8px var(--glow-color, transparent);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 5px;
    background: rgba(255, 255, 255, 0.35);
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: inherit;
    padding: 3px;
    background: conic-gradient(
      from 0deg,
      transparent 0deg,
      transparent calc((1 - var(--timer-fraction)) * 360deg),
      var(--tip-color) calc((1 - var(--timer-fraction)) * 360deg),
      var(--timer-color) calc((1 - var(--timer-fraction)) * 360deg + 4deg),
      var(--timer-color) 360deg
    );
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    pointer-events: none;
    z-index: 2;
  }

  &.no-timer::before,
  &.no-timer::after {
    display: none;
  }

  .timer-overflow-ring {
    position: absolute;
    inset: 1px;
    border-radius: inherit;
    padding: 3px;
    background: conic-gradient(
      from 0deg,
      transparent 0deg,
      transparent calc((1 - var(--timer-overflow)) * 360deg),
      var(--overflow-tip) calc((1 - var(--timer-overflow)) * 360deg),
      var(--timer-color) calc((1 - var(--timer-overflow)) * 360deg + 4deg),
      var(--timer-color) 360deg
    );
    opacity: 0.5;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    pointer-events: none;
    z-index: 3;
  }
}

.board-inner {
  position: absolute;
  inset: 5px;
  border-radius: var(--radius-x-small);
  overflow: hidden;
}

.board-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10;
}

.location-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11;
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
}

.location-overlay-text {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-white-80);
  padding: 10px 20px;
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--color-white-20);
  backdrop-filter: blur(8px);
}

.submit-retrying {
  pointer-events: none;
}

.submit-retry-spinner {
  width: 28px;
  height: 28px;
  border: 2px solid var(--color-white-20);
  border-top-color: var(--color-white-80);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.end-overlay {
  position: absolute;
  inset: 0;
  z-index: 12;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.end-overlay-timeout {
  background: #e74c3c;
}

.end-overlay-completed {
  background: #ffffff;
}

.end-overlay-text {
  font-size: 32px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: 0.02em;
}

.end-overlay-text-dark {
  color: #000000;
}

.end-overlay-icon {
  width: 28px;
  height: 28px;
  filter: brightness(0);
}

.board-cover {
  position: absolute;
  inset: 0;
  z-index: 10;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &.loading {
    pointer-events: none;
  }
}

.cover-checkerboard {
  position: absolute;
  inset: 0;
  background-image: repeating-conic-gradient(#b58863 0% 25%, #f0d9b5 0% 50%);
  background-size: 25% 25%;
}

.cover-spinner {
  position: relative;
  width: 24px;
  height: 24px;
  border: 2.5px solid rgba(0, 0, 0, 0.1);
  border-top-color: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

// --- Warmup cover ---

.warmup-cover {
  cursor: pointer;
}

.warmup-overlay-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.warmup-dim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
}

.warmup-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.warmup-speed {
  font-size: 32px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  transition: color 0.4s ease;

  &.speed-red {
    color: oklch(0.65 0.2 25);
  }

  &.speed-green {
    color: oklch(0.72 0.17 145);
  }
}

.warmup-tap-hint {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  margin: auto;
  border-radius: 50%;
  background: #fff;
  color: #1a1a1a;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  pointer-events: none;
}

.reds-tap-hint {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  margin: auto;
  border-radius: 50%;
  background: #fff;
  color: var(--color-incorrect);
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  pointer-events: none;
}

.warmup-dots {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.warmup-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transition: background 0.3s ease;

  &.filled {
    background: rgba(255, 255, 255, 0.8);
  }
}

// --- Progress dots (break board) ---

.progress-dots {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  justify-content: flex-start;
  padding: 10px;
  gap: 4px;
  pointer-events: none;
}

.progress-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;

  &.correct {
    background: oklch(0.72 0.17 145);
  }

  &.wrong {
    background: oklch(0.65 0.2 25);
  }

  &.timeout {
    background: transparent;
    border: 1.5px solid rgba(0, 0, 0, 0.35);
  }
}

.warmup-fade-leave-active {
  transition: opacity 0.5s ease;
}

.warmup-fade-leave-to {
  opacity: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// --- Walkfield Grid (Settings Mode / Shoji Style) ---

.walkfield-grid {
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(var(--zoom-n, 8), 1fr);
  grid-template-rows: repeat(var(--zoom-n, 8), 1fr);
  gap: 1px;
  border-radius: var(--radius-x-small);
  overflow: hidden;
  /* The canopy under the squares, seen through the gaps and the selected square. */
  background: url('../../../../ui/images/walk/canopy.jpg') center / cover no-repeat;

  &.loading {
    pointer-events: none;
  }

  &.inspect {
    background: var(--color-pink);
  }
}

.settings-spinner-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);

  .cover-spinner {
    border-color: rgba(255, 255, 255, 0.15);
    border-top-color: rgba(255, 255, 255, 0.7);
  }
}

.walkfield-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  background: #000;
  cursor: pointer;
  transition:
    background 150ms ease,
    box-shadow 150ms ease;
  position: relative;

  @include hover {
    filter: brightness(1.2);
  }

  &.selected {
    background: var(--color-pink-30);
    animation: none;
    box-shadow: none;
    filter: none;
  }
}

.walkfield-row-label {
  font-size: 9px;
  font-weight: 600;
  color: var(--color-white-40);
  pointer-events: none;
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
}

.walkfield-col-label {
  font-size: 9px;
  font-weight: 600;
  color: var(--color-white-40);
  pointer-events: none;
  position: absolute;
  bottom: 3px;
  left: 50%;
  transform: translateX(-50%);
}

.walkfield-selected-label {
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  pointer-events: none;
}

.walkfield-zoom-label {
  position: relative;
  font-size: calc(72px / var(--zoom-n));
  font-weight: 700;
  color: #000000;
  pointer-events: none;
  letter-spacing: 0.02em;

  &.dark {
    color: #ffffff;
  }

  &.selected {
    color: #ffffff;
  }
}

.puzzle-board.settings-mode {
  box-shadow:
    0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 0 40px 8px oklch(0.78 0.24 var(--hue) / 0.05);
}
</style>
